/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import Helmet from 'react-helmet';
import { batch, useDispatch, useSelector } from 'react-redux';
import ResizeObserver from 'react-resize-observer';

import HeaderText from '../../shared/_TBComponents/HeaderText';
import Settings from './components/Settings';
import {
  fetchGroups,
  setOnboardingModalOpen,
  setRecoveryRadarView,
} from './ducks/RecoveryRadarActions';
import { Nunito, TransitionGlobal } from '../../shared/GlobalStyles';
import Overview from './components/Overview';
import Analysis from './components/Analysis';
import History from './components/History';
import RecoveryRadarModal from './RecoveryRadarModal';
import { dashboardTracker, onboardingTracker } from '../../shared/utils/amplitudeHelper';
import OnboardingModal from '../../shared/_TBComponents/OnboardingModal';
import {
  createViewedFeaturePayload,
  hasViewedFeature,
  FEATURES,
} from '../../shared/utils/onboardingStatus';
import Icon from '../../shared/_TBComponents/Icon';
import { getModuleInfoByTitle } from '../sports-science/components/presentational/packages';
import { setViewedFeatures } from '../sports-science/ducks/sportsScienceActions';

const Container = styled('div')`
  width: 100%;
`;

const PageWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-width: 1450px;
`;

const DashboardHeader = styled('div')`
  display: flex;
  width: 100%;
  background-color: #ffffff;
  padding-top: 30px;
  padding-left: 20px;
  justify-content: space-between;

  h1 {
    padding-bottom: 8px;
  }
`;

const MenuContainer = styled('div')`
  display: flex;
`;

const MenuItem = styled('button')`
  border: none;
  margin-left: 10px;
  margin-right: 12px;
  font-family: ${Nunito};
  font-weight: 400;
  font-size: 16px;
  border-bottom: ${(props) => (props.isActive ? '3px solid #1787FB!important' : '3px solid white')};
  color: ${(props) => (!props.isActive ? '#9E9E9E' : '#444444')};
`;

const InfoWrapper = styled('div')`
  display: flex;
  border-radius: 50%;
  justify-content: center;
  align-items: center;
  height: 20px;
  width: 20px;
  border: 2px solid #9e9e9e;
  margin-left: 10px;
  margin-bottom: 8px;
  cursor: ${(props) => (props.disabled ? 'cursor' : 'pointer')};
  :hover {
    opacity: ${(props) => (props.disabled ? '1' : '.7')};
  }
  transition: ${TransitionGlobal};
  @media only screen and (max-width: 601px) {
    display: none;
  }
`;

const HeaderContainer = styled('div')`
  display: flex;
  align-items: center;
`;

const HeaderAndSubheader = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`;

const SubheaderText = styled('h2')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  margin-left: 6px;
  margin-bottom: 6px;
  color: #9e9e9e;
`;

const RecoveryRadar = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const isOnboardingModalShowing = useSelector(
    (state) => state.recoveryRadar.ui.isOnboardingModalShowing,
  );
  const recoveryRadarView = useSelector(
    (state) => state.recoveryRadar.ui.recoveryRadarView,
  );
  const [containerWidth, setContainerWidth] = useState(0);
  const viewedFeatures = useSelector((state) => state.sportsScience.data.viewedFeatures);
  const viewedFeaturesLoading = useSelector(
    (state) => state.sportsScience.ui.viewedFeaturesLoading,
  );
  const isFeatureViewed = hasViewedFeature(viewedFeatures, FEATURES.BODY_HEATMAP);
  const isSideBarOpen = useSelector((state) => state.sportsScience.data.isSideBarOpen);
  const moduleInfo = getModuleInfoByTitle('Body Heatmap');
  const { content } = moduleInfo;

  useEffect(() => {
    dispatch(setOnboardingModalOpen(false));
    if (!viewedFeaturesLoading && !isFeatureViewed && (containerWidth > 714 || !isSideBarOpen)) {
      dispatch(setOnboardingModalOpen(true));
      // Amplitude tracking
      onboardingTracker(
        'Modal Opened',
        'Recovery Radar',
        'Onboarding modal opened on user\'s first view of dashboard',
      );
    }
  }, [isFeatureViewed, viewedFeaturesLoading, dispatch, containerWidth, isSideBarOpen]);

  useEffect(() => {
    if (currentUser) {
      dispatch(fetchGroups(currentUser.accountCode));
    }
  }, [currentUser]);

  const handleMenuClick = (view) => {
    if (view !== recoveryRadarView) {
      dispatch(setRecoveryRadarView(view));
      // Amplitude tracking
      let title;
      switch (view) {
        case 'analysis':
          title = 'Group Analysis';
          break;
        case 'history':
          title = 'Athlete History';
          break;
        case 'settings':
          title = 'Settings';
          break;
        default:
          title = 'Overview';
      }
      dashboardTracker('Recovery Radar', title, 'Menu item clicked');
    }
  };

  const handleClose = () => {
    const viewedFeaturePayload = createViewedFeaturePayload(
      currentUser.accountCode,
      currentUser.id,
      FEATURES.BODY_HEATMAP,
    );
    batch(() => {
      dispatch(setOnboardingModalOpen(false));
      dispatch(setViewedFeatures(viewedFeaturePayload));
    });
  };

  const handleOpenOnboarding = () => {
    dispatch(setOnboardingModalOpen(true));
  };

  return (
    <Container>
      <ResizeObserver
        onResize={(rect) => {
          setContainerWidth(rect?.width);
        }}
      />
      <Helmet>
        <title>Body Heatmap | TeamBuildr</title>
      </Helmet>
      <PageWrapper>
        <DashboardHeader>
          <HeaderContainer>
            <HeaderAndSubheader>
              <HeaderText fontWeight='300' letterSpacing='normal'>
                Body Heatmap
              </HeaderText>
              {containerWidth > 868 && <SubheaderText>(Formerly Recovery Radar)</SubheaderText>}
            </HeaderAndSubheader>

            <InfoWrapper onClick={handleOpenOnboarding}>
              <Icon
                icon='info'
                size='12px'
                styledProps={{
                  display: 'flex',
                }}
              />
            </InfoWrapper>
          </HeaderContainer>
          <MenuContainer>
            <MenuItem
              isActive={recoveryRadarView === 'overview'}
              onClick={() => handleMenuClick('overview')}
            >
              Overview
            </MenuItem>
            <MenuItem
              isActive={recoveryRadarView === 'analysis'}
              onClick={() => handleMenuClick('analysis')}
            >
              Group Analysis
            </MenuItem>
            <MenuItem
              isActive={recoveryRadarView === 'history'}
              onClick={() => handleMenuClick('history')}
            >
              Athlete History
            </MenuItem>
            <MenuItem
              isActive={recoveryRadarView === 'settings'}
              onClick={() => handleMenuClick('settings')}
            >
              Settings
            </MenuItem>
          </MenuContainer>
        </DashboardHeader>
        {recoveryRadarView === 'overview' && <Overview />}
        {recoveryRadarView === 'analysis' && <Analysis />}
        {recoveryRadarView === 'history' && <History />}
        {recoveryRadarView === 'settings' && <Settings />}
      </PageWrapper>
      <RecoveryRadarModal />
      <OnboardingModal
        badgeText='GET STARTED'
        content={content}
        dashName='Body Heatmap'
        isActive={isOnboardingModalShowing}
        maxHeight={null}
        minHeight={null}
        onClose={handleClose}
        showSingleButton
        onPrimaryAction={handleClose}
        primaryButtonText='GET STARTED'
      />
    </Container>
  );
};

export default RecoveryRadar;
